'use es6';

import * as checked from 'reporting-data/lib/checked';
import { BasicTypes, FieldTypes, UNDEFINED_BUCKET } from '../schema/column-records';
import { getBasicTypeFormat, getColumnFormat, getColumnLabel, getCurrencyNameForColumn, isFieldExpressionBased, isMagicMeasure } from '../utils/column-utils';
import http from 'hub-http/clients/apiClient';
import I18n from 'I18n';
import { fromJS, Map as ImmutableMap } from 'immutable';
import { COLUMN_ROLES } from 'reporting-data/constants/relationalReports';
import { Metrics } from '../../shared/Metrics';
import { getSnowflakeProperties } from '../metadata/properties';
import { getResolver } from '../metadata/references/references';
import { Reference } from '../metadata/references/references-records';
import { DATASET_TABLE } from '../schema/table-records';
import { expressionFieldsListToMap, expressionFieldToSnowflakeProperty } from '../utils/expression-field-utils';
import { getDataSourceIdFromTableDescription } from '../utils/table-utils';
import { toPropertyBasicType } from '../../dataset/type-utils';
const MagicMeasureProperty = checked.record({
  name: checked.string(),
  label: checked.string(),
  type: checked.string()
}, 'MagicMeasureProperty');
export const toMagicMeasureProperty = field => MagicMeasureProperty({
  name: field.name,
  label: field.name,
  type: field.type
});
const getFieldIds = (dataset, property) => dataset.get('data').map(row => row.get(property)).toOrderedSet().remove(undefined).remove(UNDEFINED_BUCKET).map(id => String(id)).toList();
const getUndefinedReference = __snowflakeProperty => Reference({
  id: UNDEFINED_BUCKET,
  label: I18n.text('reporting-snowflake.undefined-bucket-label'),
  displayOrder: Number.MAX_SAFE_INTEGER || 9007199254740991
  // Math.pow(2, 53) - 1 === 9007199254740991
});
const getPropertyBasicType = property => {
  switch (property.type) {
    case FieldTypes.NUMBER:
    case FieldTypes.CURRENCY_NUMBER:
      return BasicTypes.NUMBER;
    case FieldTypes.BOOL:
      return BasicTypes.BOOLEAN;
    case FieldTypes.DATE:
      return BasicTypes.DATE;
    case FieldTypes.DATETIME:
      return BasicTypes.DATETIME;
    default:
      return BasicTypes.STRING;
  }
};
const accountInfoUrl = 'account-info/v1/details';
const fetchAccountInfo = () => http.get(`${accountInfoUrl}`);
const hydrateDatasetField = (field, datasetDefinition, dataset, datasetMeta, fieldAlias) => {
  if (field.derived) {
    const defaultReference = ImmutableMap({
      [UNDEFINED_BUCKET]: getUndefinedReference(undefined)
    });
    if (field.type.type === 'DATE') {
      return Promise.resolve(ImmutableMap({
        references: defaultReference,
        type: 'date'
      }));
    }
    if (toPropertyBasicType(field.type) === 'number') {
      return Promise.resolve(ImmutableMap({
        references: defaultReference,
        type: 'number'
      }));
    }
    return Promise.resolve(ImmutableMap({
      references: defaultReference
    }));
  }
  const property = datasetDefinition.properties.getIn([field.expression.table, field.expression.name]);
  const snowflakeTable = datasetMeta.tables.get(property.table);
  const snowflakeProperty = datasetMeta.properties.getIn([property.table, property.name]);
  const defaultReference = ImmutableMap({
    [UNDEFINED_BUCKET]: getUndefinedReference(snowflakeProperty)
  });
  const basicType = getPropertyBasicType(property);
  const defaultHydratedProperty = snowflakeProperty.toMap().set('type', getBasicTypeFormat(basicType, snowflakeProperty)).set('references', defaultReference);
  const resolver = getResolver(snowflakeTable, snowflakeProperty);
  if (!resolver) {
    return Promise.resolve(defaultHydratedProperty);
  }
  const referencedIds = getFieldIds(dataset, fieldAlias);
  return resolver(referencedIds).then(references => references.toMap().mapKeys((_, reference) => reference.id)).then(references => defaultHydratedProperty.update('references', prevReferences => prevReferences.merge(references))).catch(e => {
    console.error(e);
    return defaultHydratedProperty;
  });
};
const hydrateColumn = (reportMeta, column, dataset, reportDefinition) => {
  const {
    tables,
    properties,
    datasetDefinition
  } = reportMeta;
  const aggregation = column.get('aggregation');
  const isDimension = column.role === COLUMN_ROLES.DIMENSION;
  const field = column.field;
  const {
    table: tableName,
    name: fieldName
  } = field;
  const snowflakeTable = tables.get(tableName);
  const snowflakeProperty = isMagicMeasure(column) ? toMagicMeasureProperty(field) : properties.getIn([tableName, fieldName]);
  const maybeDatasetField = tableName === DATASET_TABLE && datasetDefinition.getIn(['fields', fieldName]);
  const columnLabel = getColumnLabel(column, snowflakeProperty, snowflakeTable, maybeDatasetField, reportDefinition.expressions);
  const defaultReferences = isDimension ? ImmutableMap({
    [UNDEFINED_BUCKET]: getUndefinedReference(snowflakeProperty)
  }) : ImmutableMap();
  const optionalFields = fromJS(Object.assign({}, aggregation && {
    propertyMeta: {
      aggregationType: aggregation
    }
  }));
  if (isFieldExpressionBased(column.field)) {
    const expressionField = expressionFieldsListToMap(reportDefinition.expressions).get(column.field.name);
    return Promise.resolve(ImmutableMap({
      label: columnLabel,
      name: expressionField.alias,
      type: field.type,
      hidden: false,
      references: defaultReferences,
      metaDefinition: expressionFieldToSnowflakeProperty(expressionField).toJS()
    }));
  }
  const datasetProperty = isMagicMeasure(column) ||
  // snowflake property could be undefined if hydrating with field from table __DATASET__
  snowflakeProperty && (!snowflakeProperty.metaDefinition || !snowflakeProperty.metaDefinition.currencyPropertyName) ? ImmutableMap(snowflakeProperty).set('label', columnLabel).set('type', getColumnFormat(column, snowflakeProperty)).set('references', defaultReferences).merge(optionalFields) : ImmutableMap(snowflakeProperty).set('label', columnLabel).set('type', getColumnFormat(column, snowflakeProperty)).set('references', defaultReferences).set('currencyCodeColumnName', getCurrencyNameForColumn(column.alias)).merge(optionalFields);
  if (!isDimension) {
    return Promise.resolve(datasetProperty);
  }
  if (tableName === DATASET_TABLE) {
    return hydrateDatasetField(maybeDatasetField, datasetDefinition, dataset, reportMeta, column.alias).then(hydrated => hydrated.set('label', columnLabel));
  }
  const resolver = tableName !== DATASET_TABLE && getResolver(snowflakeTable, snowflakeProperty);
  if (!resolver) {
    Metrics.counter('no_resolver', {
      type: 'catch-all'
    }).increment();
    return Promise.resolve(datasetProperty);
  }
  const referencedIds = getFieldIds(dataset, column.alias);
  return resolver(referencedIds).then(references => references.toMap().mapKeys((_, reference) => reference.id)).then(references => datasetProperty.update('references', prevReferences => prevReferences.merge(references))).catch(e => {
    console.error(e);
    return datasetProperty;
  });
};
const hydrateCompanyCurrency = (reportMeta, table, column, datasetProperty) => {
  const {
    properties
  } = reportMeta;
  const field = column.field;
  const {
    table: tableName,
    name: fieldName
  } = field;
  const dataSourceId = getDataSourceIdFromTableDescription(table, tableName);
  const snowflakeProperty = isMagicMeasure(column) ? toMagicMeasureProperty(field) : properties.getIn([tableName, fieldName]);
  const isCurrencyColumn = snowflakeProperty && !!snowflakeProperty.metaDefinition && snowflakeProperty.metaDefinition.showCurrencySymbol;
  return isCurrencyColumn ? fetchAccountInfo().then(response => getSnowflakeProperties(dataSourceId).then(snowflakeProperties => {
    if (!snowflakeProperties.has(snowflakeProperty.metaDefinition.currencyPropertyName)) {
      return datasetProperty.set('companyCurrencyCode', response.currency);
    }
    return datasetProperty;
  })).catch(e => {
    console.error(e);
    return datasetProperty;
  }) : Promise.resolve(datasetProperty);
};
export const hydrate = (reportDefinition, reportMeta, dataset) => {
  const {
    columns,
    table
  } = reportDefinition;
  try {
    return Promise.all(columns.map((column, columnAlias) => hydrateColumn(reportMeta, column, dataset, reportDefinition).then(datasetProperty => {
      if (isMagicMeasure(column)) {
        return datasetProperty;
      }
      return hydrateCompanyCurrency(reportMeta, table, column, datasetProperty);
    }).then(datasetProperty => [columnAlias, datasetProperty])).toList()).then(result => ImmutableMap(result)).then(propertiesWithReferences => dataset.update('properties', datasetProperties => datasetProperties.merge(propertiesWithReferences))).catch(e => {
      console.error(e);
      return dataset;
    });
  } catch (e) {
    console.error(e);
    return Promise.resolve(dataset);
  }
};